import { GridReadyEvent } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { JoHeader, PoHeader, PoNote } from 'app/model/entities/entity-model';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';

import { PoStatusCode } from 'app/model/enums/po-status-code';
import { DateFns } from 'app/shared/date-fns';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { EntityFns } from 'app/shared/entity-fns';
import _ from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface SplitPurchaseOrdersDialogParams extends DialogParams {
  poHeader: PoHeader;
}

@Component({
  templateUrl: './split-purchase-orders-dialog.component.html',
  standalone: false,
})
export class SplitPurchaseOrdersDialogComponent extends DialogBaseComponent<SplitPurchaseOrdersDialogParams> {
  poHeader: PoHeader;

  joGridOptions: GridOptionsExt;
  jobOrders: JoHeader[];

  shouldSplit = '_shouldSplit';

  constructor(
    public config: DynamicDialogConfig<SplitPurchaseOrdersDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: 'Select job orders to split off',
      width: '700px',
      height: '480px',
    });
    this.prepare();
  }

  async prepare() {
    this.poHeader = this.params.poHeader;
    this.joGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJoGridReady,
    });

    await this.getRelatedJobOrders();
    this.isPageReady = true;
  }

  async getRelatedJobOrders() {
    if (this.poHeader.poStatusId !== PoStatusCode.NewUnposted) {
      await this.dialogService.showOkMessage(
        'Invalid Operation',
        'You cannot split a purchaseOrder that has a status of: ' + this.poHeader.poStatus.name,
      );
      this.dialogRef.close(null);
      return;
    }
    let poDetails = this.poHeader.poDetails;
    if (poDetails.length == 0) {
      poDetails = await this.dbQueryService.getPoDetailsForReceive(this.poHeader.id);
    }

    this.jobOrders = _.chain(poDetails)
      .map((pod) => pod.joDetail)
      .filter((jod) => jod != null)
      .map((jod) => jod.joHeader)
      .uniq()
      .value();

    // just in case;
    this.jobOrders.forEach((jo) => (jo[this.shouldSplit] = null));
  }

  onJoGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Job Order', field: 'id' },
      {
        ...AgFns.createCheckboxProps('Split', this.shouldSplit, 'splitCheckBox'),
        valueGetter: (p) => !!p.data[this.shouldSplit],
        valueSetter: (p) => (p.data[this.shouldSplit] = p.newValue),
        editable: true,
      },

      { headerName: 'Account', field: 'account.accountName' },
      { headerName: 'J/O Date', field: 'joDate' },
    ];
    AgFns.initGrid(event, colDefs);
    AgFns.sizeColumnsToFit(this.joGridOptions);
  }

  async ok() {
    this.joGridOptions.apiX.stopEditing();

    const joIdsToSplit = this.jobOrders.filter((po) => po[this.shouldSplit]).map((jo) => jo.id);

    if (joIdsToSplit.length === 0) {
      await this.dialogService.showOkMessage('Select at least one', 'You must select at least one Job Order to split off.');
      return false;
    } else if (joIdsToSplit.length === this.jobOrders.length) {
      await this.dialogService.showOkMessage(
        'Cannot select all',
        `You cannot select all of the Job Orders to be split off.
      You must leave at least 1 job order on the original Purchase Order`,
      );
      return false;
    }

    const clone = EntityFns.cloneStruct(this.poHeader, ['id', 'crtnTs']);

    const newPoHeader = this.uow.createEntity(PoHeader, clone);
    const now = new Date();
    newPoHeader.crtnTs = now;

    this.poHeader.poNotes.forEach((pon) => {
      // copy all of the notes that are NOT poDetail specific.
      if (pon.poDetailId == null) {
        const ponClone = EntityFns.cloneStruct(pon, ['id']);
        const newPoNote = this.uow.createEntity(PoNote, ponClone);
        newPoNote.poHeader = newPoHeader;
      }
    });

    const podsToSplit = this.poHeader.poDetails.filter((pod) => {
      return pod.joDetail != null && joIdsToSplit.indexOf(pod.joDetail.joHeaderId) !== -1;
    });

    podsToSplit.slice().forEach((pod) => {
      pod.poNotes.slice().forEach((pon) => (pon.poHeader = newPoHeader));
      pod.modTs = new Date();
      pod.poHeader = newPoHeader;
    });

    // poke crtn ts by 1 second for Optimistic concurrency
    this.poHeader.crtnTs = DateFns.pokeDate(this.poHeader.crtnTs);

    await this.dbSaveService.saveChanges();
    this.dialogRef.close(true);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
